// 数据表格组件
// 可复用的数据表格，支持排序、筛选、分页等功能
import React, { useState, useMemo, useCallback } from 'react';
import {
  Table,
  Space,
  Button,
  Tooltip,
  Typography,
  Tag,
  Dropdown,
  Menu,
  Input
} from 'antd';
import {
  MoreOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  SearchOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
// Removed unused Resizable import
import { FIELD_MAPPING } from '../../config/field-mapping';
import { FieldMappingUtils } from '../../utils/field-mapping-utils';
import { FieldConfig } from '../../types/field-mapping';
import { DataFormatter } from '../../utils/formatters';
import './DataTable.css';

const { Text } = Typography;

// Removed unused ResizableTitle component

export interface DataTableColumn {
  key: string;
  title: string;
  dataIndex: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
  sorter?: boolean;
  filterable?: boolean;
  fixed?: 'left' | 'right';
  render?: (value: any, record: any, index: number) => React.ReactNode;
  fieldConfig?: FieldConfig;
}

export interface DataTableProps {
  columns: DataTableColumn[];
  dataSource: any[];
  loading?: boolean;
  pagination?: any;
  rowSelection?: any;
  scroll?: { x?: number; y?: number };
  size?: 'small' | 'middle' | 'large';
  bordered?: boolean;
  sticky?: boolean;
  onChange?: (pagination: any, filters: any, sorter: any) => void;
  onRow?: (record: any, index?: number) => any;
  summary?: () => React.ReactNode;
  showColumnConfig?: boolean;
  onColumnConfigChange?: (visibleColumns: string[]) => void;
  density?: 'compact' | 'default' | 'comfortable';
  onDensityChange?: (density: 'compact' | 'default' | 'comfortable') => void;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  dataSource,
  loading = false,
  pagination,
  rowSelection,
  scroll = { x: 'max-content', y: 600 },
  size = 'small',
  bordered = true,
  sticky = true,
  onChange,
  onRow,
  summary,
  showColumnConfig = true,
  onColumnConfigChange,
  density = 'default',
  onDensityChange
}) => {
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    columns.map(col => col.key)
  );

  // 列宽状态管理
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const initialWidths: Record<string, number> = {};
    columns.forEach(col => {
      if (col.fieldConfig) {
        // 根据字段类型设置智能默认宽度
        switch (col.fieldConfig.type) {
          case 'currency':
            initialWidths[col.key] = 140;
            break;
          case 'number':
            initialWidths[col.key] = 120;
            break;
          case 'percentage':
            initialWidths[col.key] = 100;
            break;
          case 'date':
          case 'datetime':
            initialWidths[col.key] = 130;
            break;
          case 'select':
            initialWidths[col.key] = 120;
            break;
          default:
            // 根据标题长度动态计算
            initialWidths[col.key] = Math.max(100, col.title.length * 14 + 60);
        }
      } else {
        initialWidths[col.key] = col.width || 120;
      }
    });
    return initialWidths;
  });

  // 处理列宽调整
  const handleResize = useCallback((key: string) => (width: number) => {
    setColumnWidths(prev => ({
      ...prev,
      [key]: Math.max(80, width) // 最小宽度80px
    }));
  }, []);

  // 构建表格列配置
  const tableColumns = useMemo(() => {
    return columns
      .filter(col => visibleColumns.includes(col.key))
      .map(col => {
        const currentWidth = columnWidths[col.key];
        const fieldConfig = col.fieldConfig;

        // 根据字段类型确定对齐方式
        const getAlignment = () => {
          if (col.align) return col.align;
          if (!fieldConfig) return 'left';

          switch (fieldConfig.type) {
            case 'currency':
            case 'number':
            case 'percentage':
              return 'right';
            case 'date':
            case 'datetime':
            case 'select':
              return 'center';
            default:
              return 'left';
          }
        };

        // 生成列的CSS类名
        const getColumnClassName = () => {
          const classes = [];
          if (fieldConfig?.type) {
            classes.push(`column-${fieldConfig.type}`);
          }
          if (fieldConfig?.required) {
            classes.push('column-important');
          }
          if (col.key === 'factoryName' || col.key === 'productName') {
            classes.push('column-primary');
          }
          return classes.join(' ');
        };

        const column: any = {
          title: FIELD_MAPPING[col.key]?.label || col.title, // 使用映射翻译标题，找不到则回退
          dataIndex: col.dataIndex,
          key: col.key,
          width: currentWidth,
          align: getAlignment(),
          fixed: col.fixed,
          className: getColumnClassName(),
          ellipsis: {
            showTitle: false, // 禁用默认title，使用自定义Tooltip
          },
          // 暂时禁用onHeaderCell以确保筛选图标显示
          // onHeaderCell: () => ({
          //   width: currentWidth,
          //   onResize: handleResize(col.key),
          //   className: getColumnClassName(),
          // }),
          onCell: () => ({
            className: getColumnClassName(),
          }),
        };

        // 设置排序功能
        if (col.sorter && col.fieldConfig?.sortable) {
          if (col.fieldConfig.type === 'number' || col.fieldConfig.type === 'currency' || col.fieldConfig.type === 'percentage') {
            // 数字类型排序
            column.sorter = (a: any, b: any) => {
              const aVal = parseFloat(a[col.dataIndex]) || 0;
              const bVal = parseFloat(b[col.dataIndex]) || 0;
              return aVal - bVal;
            };
          } else if (col.fieldConfig.type === 'date' || col.fieldConfig.type === 'datetime') {
            // 日期类型排序
            column.sorter = (a: any, b: any) => {
              const aVal = new Date(a[col.dataIndex]).getTime() || 0;
              const bVal = new Date(b[col.dataIndex]).getTime() || 0;
              return aVal - bVal;
            };
          } else {
            // 文本类型排序
            column.sorter = (a: any, b: any) => {
              const aVal = String(a[col.dataIndex] || '');
              const bVal = String(b[col.dataIndex] || '');
              return aVal.localeCompare(bVal, 'zh-CN');
            };
          }
        }

        // 设置筛选功能
        if (col.filterable && col.fieldConfig?.filterable) {
          if (col.fieldConfig.options) {
            // 有预定义选项的字段（如工厂名称、成本类型）
            column.filters = col.fieldConfig.options.map(opt => ({
              text: opt.label,
              value: opt.value
            }));
            column.onFilter = (value: any, record: any) => record[col.dataIndex] === value;
          } else if (col.fieldConfig.type === 'select') {
            // 动态生成筛选选项（从数据中提取唯一值）
            column.filterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
              <div style={{ padding: 8 }}>
                <Input
                  placeholder={`搜索 ${col.title}`}
                  value={selectedKeys[0]}
                  onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                  onPressEnter={() => confirm()}
                  style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={() => confirm()}
                    icon={<SearchOutlined />}
                    size="small"
                    style={{ width: 90 }}
                  >
                    搜索
                  </Button>
                  <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
                    重置
                  </Button>
                </Space>
              </div>
            );
            column.filterIcon = (filtered: boolean) => (
              <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            );
            column.onFilter = (value: any, record: any) =>
              record[col.dataIndex]?.toString().toLowerCase().includes(value.toLowerCase());
          }
        }

        // 设置渲染函数
        column.render = col.render || ((value: any) => {
          if (col.fieldConfig) {
            const cellValue = DataFormatter.formatCellValue(value, col.fieldConfig);
            return (
              <Tooltip title={cellValue.text} placement="topLeft">
                <span style={{
                  color: cellValue.color,
                  backgroundColor: cellValue.backgroundColor,
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {cellValue.text}
                </span>
              </Tooltip>
            );
          }
          return (
            <Tooltip title={value} placement="topLeft">
              <span style={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {value}
              </span>
            </Tooltip>
          );
        });

        // 设置排序功能
        if (col.sorter && col.fieldConfig?.sortable) {
          if (col.fieldConfig.type === 'number' || col.fieldConfig.type === 'currency' || col.fieldConfig.type === 'percentage') {
            // 数字类型排序
            column.sorter = (a: any, b: any) => {
              const aVal = parseFloat(a?.[col.dataIndex]) || 0;
              const bVal = parseFloat(b?.[col.dataIndex]) || 0;
              return aVal - bVal;
            };
          } else if (col.fieldConfig.type === 'date' || col.fieldConfig.type === 'datetime') {
            // 日期类型排序
            column.sorter = (a: any, b: any) => {
              const aVal = new Date(a?.[col.dataIndex]).getTime() || 0;
              const bVal = new Date(b?.[col.dataIndex]).getTime() || 0;
              return aVal - bVal;
            };
          } else {
            // 文本类型排序
            column.sorter = (a: any, b: any) => {
              const aVal = String(a?.[col.dataIndex] || '');
              const bVal = String(b?.[col.dataIndex] || '');
              return aVal.localeCompare(bVal, 'zh-CN');
            };
          }
        }

        // 为可筛选字段添加筛选功能，确保类型安全
        if (col.filterable && col.fieldConfig) {
          // 工厂名称筛选
          if (col.key === 'factoryName') {
            column.filters = [
              { text: '太元', value: '太元' },
              { text: '调理一厂', value: '调理一厂' },
              { text: '调理二厂', value: '调理二厂' },
            ];
            column.onFilter = (value: any, record: any) => record?.[col.dataIndex] === value;
          }
          // 成本类型筛选
          else if (col.key === 'costType') {
            column.filters = [
              { text: '内销', value: '内销' },
              { text: '出口', value: '出口' },
            ];
            column.onFilter = (value: any, record: any) => record?.[col.dataIndex] === value;
          }
          // 月份筛选
          else if (col.key === 'month') {
            // 从数据中提取唯一月份，确保类型安全
            const uniqueMonths = [...new Set(
              dataSource
                .map(item => item?.[col.dataIndex])
                .filter((value): value is string => Boolean(value) && typeof value === 'string')
            )].sort();

            if (uniqueMonths.length > 0) {
              column.filters = uniqueMonths.map(month => ({
                text: month,
                value: month
              }));
              column.onFilter = (value: any, record: any) => record?.[col.dataIndex] === value;
            }
          }
          // 其他字段的通用筛选
          else {
            column.filterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
              <div style={{ padding: 8 }}>
                <Input
                  placeholder={`搜索 ${col.title}`}
                  value={selectedKeys[0]}
                  onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                  onPressEnter={() => confirm()}
                  style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                  <Button
                    type="primary"
                    onClick={() => confirm()}
                    icon={<SearchOutlined />}
                    size="small"
                    style={{ width: 90 }}
                  >
                    搜索
                  </Button>
                  <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
                    重置
                  </Button>
                </Space>
              </div>
            );
            column.filterIcon = (filtered: boolean) => (
              <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            );
            column.onFilter = (value: any, record: any) => {
              const recordValue = record?.[col.dataIndex];
              return recordValue?.toString().toLowerCase().includes(value.toLowerCase()) ?? false;
            };
          }
        }

        return column;
      });
  }, [columns, visibleColumns, columnWidths, handleResize, dataSource]);

  // 列配置菜单 - 显示所有可用字段，而不仅仅是当前可见的字段
  const columnConfigMenu = useMemo(() => {
    // 获取所有可用字段
    const allFields = FieldMappingUtils.getAllFields();

    return (
      <Menu>
        {allFields.map(field => (
          <Menu.Item key={field.key}>
            <Space>
              {visibleColumns.includes(field.key) ? (
                <EyeOutlined style={{ color: '#52c41a' }} />
              ) : (
                <EyeInvisibleOutlined style={{ color: '#999' }} />
              )}
              <span
                onClick={() => {
                  const newVisibleColumns = visibleColumns.includes(field.key)
                    ? visibleColumns.filter(key => key !== field.key)
                    : [...visibleColumns, field.key];
                  setVisibleColumns(newVisibleColumns);
                  onColumnConfigChange?.(newVisibleColumns);
                }}
                style={{ cursor: 'pointer' }}
              >
                {field.label}
              </span>
            </Space>
          </Menu.Item>
        ))}
      </Menu>
    );
  }, [visibleColumns, setVisibleColumns, onColumnConfigChange]);

  // 密度控制菜单
  const densityMenu = useMemo(() => (
    <Menu
      selectedKeys={[density]}
      onClick={({ key }) => onDensityChange?.(key as any)}
    >
      <Menu.Item key="compact">紧凑</Menu.Item>
      <Menu.Item key="default">默认</Menu.Item>
      <Menu.Item key="comfortable">舒适</Menu.Item>
    </Menu>
  ), [density, onDensityChange]);

  // 表格标题
  const tableTitle = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Space>
        <Text strong>数据列表</Text>
        <Tag color="blue">{dataSource.length} 条记录</Tag>
      </Space>

      {showColumnConfig && (
        <Space>
          {onDensityChange && (
            <Dropdown overlay={densityMenu} trigger={['click']}>
              <Button size="small" icon={<AppstoreOutlined />}>
                密度
              </Button>
            </Dropdown>
          )}
          <Dropdown overlay={columnConfigMenu} trigger={['click']}>
            <Button size="small" icon={<MoreOutlined />}>
              列配置
            </Button>
          </Dropdown>
        </Space>
      )}
    </div>
  );

  // 表格底部信息
  const tableFooter = () => (
    <div style={{ textAlign: 'center', padding: '8px 0' }}>
      <Space>
        <Text type="secondary">
          显示 {visibleColumns.length} / {columns.length} 列
        </Text>
        <Text type="secondary">|</Text>
        <Text type="secondary">
          共 {dataSource.length} 条记录
        </Text>
      </Space>
    </div>
  );

  return (
    <Table
      columns={tableColumns}
      dataSource={dataSource}
      loading={loading}
      pagination={pagination}
      rowSelection={rowSelection}
      scroll={scroll}
      size={size}
      bordered={bordered}
      sticky={sticky}
      onChange={onChange}
      onRow={onRow}
      title={tableTitle}
      footer={tableFooter}
      summary={summary}
      className={`data-table ${density !== 'default' ? `density-${density}` : ''}`}
      showSorterTooltip={false}
      rowClassName={(_, index) => {
        // 为奇偶行添加不同的背景色
        return index % 2 === 0 ? 'table-row-even' : 'table-row-odd';
      }}
      // 暂时禁用自定义header组件，以确保筛选图标正常显示
      // components={{
      //   header: {
      //     cell: (props: any) => {
      //       // 保留原始的筛选和排序功能，只在需要时添加调整大小功能
      //       const { onResize, width, ...restProps } = props;

      //       if (!width || !onResize) {
      //         // 如果没有调整大小功能，直接返回原始的th元素
      //         return (
      //           <th
      //             {...restProps}
      //             style={{
      //               ...restProps.style,
      //               background: '#fafafa',
      //               fontWeight: 600,
      //               borderBottom: '2px solid #f0f0f0',
      //               position: 'relative'
      //             }}
      //           />
      //         );
      //       }

      //       // 有调整大小功能时，使用ResizableTitle
      //       return (
      //         <ResizableTitle
      //           {...props}
      //           style={{
      //             ...props.style,
      //             background: '#fafafa',
      //             fontWeight: 600,
      //             borderBottom: '2px solid #f0f0f0',
      //             position: 'relative'
      //           }}
      //         />
      //       );
      //     }
      //   }
      // }}
    />
  );
};

export default DataTable;
